import { AssignmentStatus, Course, CourseEnrollment, EnrollmentStatus, Tag, UserGroup } from '../apis/api-client';

export type PivotMode = 'group' | 'person' | 'course' | 'tag';
export type ActivityPivotMode = 'person' | 'course';

export interface CellData {
  status: number | null;  // AssignmentStatus or EnrollmentStatus depending on context
  count: number;
  completedCount: number;
  rows: CourseEnrollment[];
}

export interface HeatmapData {
  rowKeys: string[];
  colKeys: string[];
  matrix: Record<string, Record<string, CellData>>;
}

// Lower priority number = more urgent (worst status wins when aggregating)
const STATUS_PRIORITY: Record<number, number> = {
  [AssignmentStatus.Overdue]: 0,
  [AssignmentStatus.Active]: 1,
  [AssignmentStatus.Upcoming]: 2,
  [AssignmentStatus.Completed]: 3,
};

// For enrollment status: NotStarted is most concerning, Completed is best
const ACTIVITY_STATUS_PRIORITY: Record<number, number> = {
  [EnrollmentStatus.NotStarted]: 0,
  [EnrollmentStatus.InProgress]: 1,
  [EnrollmentStatus.Completed]: 2,
};

function worstStatus(
  a: number | null,
  b: number | null,
  priority: Record<number, number>
): number | null {
  if (a === null) return b;
  if (b === null) return a;
  const pa = priority[a] ?? 99;
  const pb = priority[b] ?? 99;
  return pa <= pb ? a : b;
}

function addToMatrix(
  matrix: Record<string, Record<string, CellData>>,
  rowKey: string,
  colKey: string,
  row: CourseEnrollment,
  status: number | null,
  priority: Record<number, number>,
  completedValue: number
): void {
  if (!matrix[rowKey]) matrix[rowKey] = {};
  const cell = matrix[rowKey][colKey];
  if (!cell) {
    matrix[rowKey][colKey] = {
      status,
      count: 1,
      completedCount: status === completedValue ? 1 : 0,
      rows: [row],
    };
  } else {
    cell.status = worstStatus(cell.status, status, priority);
    cell.count += 1;
    if (status === completedValue) cell.completedCount += 1;
    cell.rows.push(row);
  }
}

// Build Map<userId, groupName[]> from flat membership list
export function buildUserGroupMap(memberships: UserGroup[]): Map<number, string[]> {
  const map = new Map<number, string[]>();
  for (const m of memberships) {
    if (!m.userId || !m.groupName) continue;
    const groups = map.get(m.userId) ?? [];
    groups.push(m.groupName);
    map.set(m.userId, groups);
  }
  return map;
}

export function buildHeatmap(
  enrollments: CourseEnrollment[],
  pivotBy: PivotMode,
  groupMemberships: UserGroup[],
  courses: Course[],
  tags: Tag[]
): HeatmapData {
  const userGroupMap = buildUserGroupMap(groupMemberships);
  const courseTagMap = new Map<number, number[]>(
    courses.map(c => [c.id!, c.tagIds ?? []])
  );
  const tagMap = new Map(tags.map(t => [t.id!, t.name ?? `Tag ${t.id}`]));

  const matrix: Record<string, Record<string, CellData>> = {};

  for (const row of enrollments) {
    const userGroups = row.userId ? (userGroupMap.get(row.userId) ?? []) : [];
    const groupColKeys = userGroups.length > 0 ? userGroups : ['No Group'];
    const status = row.assignmentStatus ?? null;

    if (pivotBy === 'group') {
      const colKey = row.courseTitle ?? `Course ${row.courseId}`;
      for (const groupName of groupColKeys) {
        addToMatrix(matrix, groupName, colKey, row, status, STATUS_PRIORITY, AssignmentStatus.Completed);
      }
    } else if (pivotBy === 'person') {
      const rowKey = `${row.lastName ?? ''}, ${row.firstName ?? ''} (${row.email ?? ''})`.trim();
      const colKey = row.courseTitle ?? `Course ${row.courseId}`;
      addToMatrix(matrix, rowKey, colKey, row, status, STATUS_PRIORITY, AssignmentStatus.Completed);
    } else if (pivotBy === 'course') {
      const rowKey = row.courseTitle ?? `Course ${row.courseId}`;
      for (const groupName of groupColKeys) {
        addToMatrix(matrix, rowKey, groupName, row, status, STATUS_PRIORITY, AssignmentStatus.Completed);
      }
    } else if (pivotBy === 'tag') {
      // Skip tag IDs not present in tagMap — caller filters out account-level tags.
      const tagIds = (courseTagMap.get(row.courseId!) ?? []).filter(id => tagMap.has(id));
      for (const groupName of groupColKeys) {
        if (tagIds.length === 0) {
          addToMatrix(matrix, 'Untagged', groupName, row, status, STATUS_PRIORITY, AssignmentStatus.Completed);
        } else {
          for (const tagId of tagIds) {
            const rowKey = tagMap.get(tagId)!;
            addToMatrix(matrix, rowKey, groupName, row, status, STATUS_PRIORITY, AssignmentStatus.Completed);
          }
        }
      }
    }
  }

  const rowKeys = Object.keys(matrix).sort();
  const colKeys = Array.from(
    new Set(rowKeys.flatMap(rk => Object.keys(matrix[rk])))
  ).sort();

  return { rowKeys, colKeys, matrix };
}

export function buildActivityHeatmap(
  enrollments: CourseEnrollment[],
  pivotBy: ActivityPivotMode,
  groupMemberships: UserGroup[]
): HeatmapData {
  const userGroupMap = buildUserGroupMap(groupMemberships);
  const matrix: Record<string, Record<string, CellData>> = {};

  for (const row of enrollments) {
    const status = row.enrollmentStatus ?? null;
    const userGroups = row.userId ? (userGroupMap.get(row.userId) ?? []) : [];
    const groupColKeys = userGroups.length > 0 ? userGroups : ['No Group'];

    if (pivotBy === 'person') {
      const rowKey = `${row.lastName ?? ''}, ${row.firstName ?? ''} (${row.email ?? ''})`.trim();
      const colKey = row.courseTitle ?? `Course ${row.courseId}`;
      addToMatrix(matrix, rowKey, colKey, row, status, ACTIVITY_STATUS_PRIORITY, EnrollmentStatus.Completed);
    } else if (pivotBy === 'course') {
      const rowKey = row.courseTitle ?? `Course ${row.courseId}`;
      for (const groupName of groupColKeys) {
        addToMatrix(matrix, rowKey, groupName, row, status, ACTIVITY_STATUS_PRIORITY, EnrollmentStatus.Completed);
      }
    }
  }

  const rowKeys = Object.keys(matrix).sort();
  const colKeys = Array.from(
    new Set(rowKeys.flatMap(rk => Object.keys(matrix[rk])))
  ).sort();

  return { rowKeys, colKeys, matrix };
}

export function statusColor(status: number | null): string {
  switch (status) {
    case AssignmentStatus.Overdue:   return 'error.light';
    case AssignmentStatus.Active:    return 'warning.light';
    case AssignmentStatus.Upcoming:  return 'info.light';
    case AssignmentStatus.Completed: return 'success.light';
    default: return 'grey.100';
  }
}

export function statusLabel(status: number | null): string {
  switch (status) {
    case AssignmentStatus.Overdue:   return 'Overdue';
    case AssignmentStatus.Active:    return 'In Progress';
    case AssignmentStatus.Upcoming:  return 'Upcoming';
    case AssignmentStatus.Completed: return 'Completed';
    default: return '—';
  }
}

/**
 * Pastel palette for the activity-report status chip. Same family as
 * the User-page status pills (mint for Completed, soft amber for
 * In Progress, neutral grey for Not Started) so the admin shell reads
 * as one consistent visual language. Returns both bg + text color so
 * the label stays legible at chip-small sizes.
 */
export function activityStatusPalette(status: number | null): { bgcolor: string; color: string } {
  switch (status) {
    case EnrollmentStatus.Completed:  return { bgcolor: '#e6f7ec', color: '#1e7a3a' };
    case EnrollmentStatus.InProgress: return { bgcolor: '#e3f2fd', color: '#0d47a1' };
    case EnrollmentStatus.NotStarted: return { bgcolor: '#f1f3f5', color: '#5a5f66' };
    default: return { bgcolor: '#f1f3f5', color: '#5a5f66' };
  }
}

export function activityStatusLabel(status: number | null): string {
  switch (status) {
    case EnrollmentStatus.Completed:  return 'Completed';
    case EnrollmentStatus.InProgress: return 'In Progress';
    case EnrollmentStatus.NotStarted: return 'Not Started';
    default: return '—';
  }
}

export function cellPercent(cell: CellData): string {
  if (cell.count === 0) return '—';
  return `${Math.round((cell.completedCount / cell.count) * 100)}%`;
}

// Aggregate a set of enrollments into a single deadline label.
// Returns null if no rows have a deadline, the formatted date if they all share one, else "Varies".
export function aggregateDeadline(rows: CourseEnrollment[]): string | null {
  const dates = rows
    .map(r => r.deadline)
    .filter((d): d is Date => d != null)
    .map(d => new Date(d).toDateString());
  if (dates.length === 0) return null;
  const uniq = new Set(dates);
  if (uniq.size === 1) return new Date(dates[0]).toLocaleDateString();
  return 'Varies';
}

export function daysLabel(deadline: Date | undefined): { text: string; isOverdue: boolean } {
  if (!deadline) return { text: 'No deadline', isOverdue: false };
  const days = Math.ceil((new Date(deadline).getTime() - Date.now()) / 86400000);
  if (days < 0) return { text: `Overdue by ${Math.abs(days)} day${Math.abs(days) !== 1 ? 's' : ''}`, isOverdue: true };
  if (days === 0) return { text: 'Due today', isOverdue: false };
  return { text: `${days} day${days !== 1 ? 's' : ''}`, isOverdue: false };
}
